import type { TextProps } from '@chakra-ui/react'
import React from 'react'

import { noOp } from '@/utils/helpers'
import type { FileUploadTarget } from '@/server/services/userUpload'

import type { SerializedRichText } from '@/client/services/richText/types'
import type {
  SATQuestionDifficulty,
  SATQuestionFocusSkill,
} from '@/client/services/satBootcamp/focusSkills'

export type FormDirection = 'horizontal' | 'vertical'
export type FormValue =
  | string
  | string[]
  | boolean
  | Date
  | null
  | SerializedRichText
  | number
  | number[]
export type FormValues = Record<string, FormValue>
export type FormErrors = Record<string, string>
export type FormDependency = {
  keys: string[]
  callback: (values: FormValues) => boolean
}

export type FormContent = string | React.ReactNode | SerializedRichText | null

export type FormBlockBase = {
  // note that undefined is essentially 'input' type, but undefined is
  // backwards compatible
  display?: 'display' | 'result' | 'input' | undefined
  type: string
  name: string
  dependencies?: FormDependency
  size: 1 | 2 | 3 | 4 | 5 | 6
  sizeSpan?: 1 | 2 | 3 | 4 | 5 | 6
  // ^ Reserves that much space from the row, but only occupies .size
  // ^ must be larger than .size, ie. size: 3, sizeSpan: 6
}

export interface FormQuestionBase extends FormBlockBase {
  display?: 'result' | 'input' | undefined
  type: string
  label: FormContent
  disabled?: boolean
  sublabel?: FormContent
  // ^ shown below the label
  description?: FormContent
  // ^ shown below the input itself
  required?: boolean | null
  // ^ names of inputs to depend on, and a callback that uses them
  // to return a go / no go
  Component?: unknown
  hideRequiredStar?: boolean
  labelFontSize?: TextProps['fontSize']

  // TODO: Eventually separate this into an Assessment FormInput type
  metadata?: {
    answers?: FormValue[] | null
    answerExplanation?: FormContent | null
    focusSkills?: SATQuestionFocusSkill[]
    difficulty?: SATQuestionDifficulty | null
  }
}

export interface FormDisplayBlockBase extends FormBlockBase {
  display: 'display'
}

export interface FormContentBlock extends FormDisplayBlockBase {
  type: 'content'
  content: FormContent
}

// This is a base "mixin" type, do not export it outside this file please
interface FormQuestionWithLength extends FormQuestionBase {
  minLength?: number | null
  maxLength?: number | null
}

export interface FormQuestionText extends FormQuestionWithLength {
  type: 'text'
  initialValue?: string | null
  placeholder?: string | null
  onChange?: (v: string) => void
}

export interface FormQuestionTextarea extends FormQuestionWithLength {
  type: 'textarea'
  initialValue?: string | null
  rows?: number | null
  placeholder?: string | null
}

export interface FormQuestionRichText extends FormQuestionWithLength {
  type: 'rich-text'
  editorType?: 'advanced' | 'simple'
  height?: string
  maxHeight?: string
  initialValue?: SerializedRichText | string | null
  placeholder?: string | null
}

export type Option = {
  label: string | SerializedRichText | NonNullable<React.ReactNode>
  disabled?: boolean
  value: string
}

// Since zod doesn't support readonly properties, and we'd like to pass
// form inputs through our API, we use our own copies of Options and
// GroupBase from 'react-select' that don't involve readonly properties.
// This shouldn't be an issue since the types are very basic and typescript
// compiler will fail if we're ever passing malformed options into
// FormInputsSelect, in which case we just need to update Options and GroupBase
// to match those in 'react-select'.
export type Options = Option[]
export type GroupBase = {
  options: Options
  label?: string
}

type FormQuestionSelectBase = FormQuestionBase & {
  isLoading?: boolean
  initialValue?: string | null
  placeholder?: string | null
  onChange?: (v: string) => void
}

export type FormQuestionSelect = FormQuestionSelectBase & {
  type: 'select'
  options: Options
}

export type FormQuestionSelectGroup = FormQuestionSelectBase & {
  type: 'select-group'
  options: GroupBase[]
}

export type FormQuestionSelectAsync = FormQuestionSelectBase & {
  type: 'select-async'
  loadOptions: (inputValue: string) => Promise<Options>
  defaultOptions?: Options | null
}

export type FormQuestionSelectTab = FormQuestionBase & {
  type: 'select-tab'
  initialValue?: string | null
  options: {
    // NOTE: The implementation supports any value for this value field,
    // but because of FormBuilder's implementation, we only support string
    // values for now. If we want to support other values, we need to
    // support them in "multi-checkbox" type also
    value: string
    label: string | number
  }[]
  onChange?: (v: string | null) => void
}

export interface FormQuestionMultiCheckbox extends FormQuestionBase {
  type: 'multi-checkbox'
  direction?: FormDirection
  initialValue?: string[] | null
  options: (Option & { disabledTooltipText?: string })[]
  onChange?: (v: string[]) => void
  isLoading?: boolean
  size: 6
}

export interface FormQuestionMultiSelect extends FormQuestionBase {
  type: 'multi-select'
  initialValue?: string[] | null
  options: Option[]
}

export type FormQuestionMultiSelectTab = FormQuestionBase & {
  type: 'multi-select-tab'
  initialValue?: string[] | null
  options: {
    // NOTE: The implementation supports any value for this value field,
    // but because of FormBuilder's implementation, we only support string
    // values for now. If we want to support other values, we need to
    // support them in "multi-checkbox" type also
    value: string
    label: string | number
  }[]
  onChange?: (v: string[]) => void
}

export interface FormQuestionRadio extends FormQuestionBase {
  type: 'radio'
  direction?: FormDirection
  initialValue?: string | null
  options: Option[]
  isLoading?: boolean
  onChange?: (v: string | null) => void
}

interface FormQuestionDate extends FormQuestionBase {
  type: 'date'
  minDate?: string | null
  maxDate?: string | null
  initialValue?: string | null // Initial value should be ISO datetime
  onChange?: (v: string | null) => void
}

interface FormQuestionNumber extends FormQuestionBase {
  type: 'number'
  min?: number | undefined
  max?: number | undefined
  initialValue?: number | null
  placeholder?: string | null
}

interface FormQuestionPhone extends FormQuestionBase {
  type: 'phone'
  initialValue?: string | null
  placeholder?: string | null
}

interface FormQuestionEquation extends FormQuestionBase {
  type: 'equation'
  initialValue?: string | null
  placeholder?: string | null
}

interface FormQuestionTimeRange extends FormQuestionBase {
  type: 'time-range'
  initialValue?: number[] | null
  onChange?: (value: number[]) => void
}

interface FormQuestionVideo extends FormQuestionBase {
  type: 'video'
  target: FileUploadTarget
  instructions: SerializedRichText
  instructionsHint?: SerializedRichText
  initialValue?: string | null
  maxDurationSeconds?: number
  minDurationSeconds?: number
  onChange?: (value: string) => void
}

type FormQuestionType =
  | FormQuestionText
  | FormQuestionTextarea
  | FormQuestionMultiCheckbox
  | FormQuestionMultiSelect
  | FormQuestionRadio
  | FormQuestionDate
  | FormQuestionNumber
  | FormQuestionSelect
  | FormQuestionSelectGroup
  | FormQuestionSelectTab
  | FormQuestionSelectAsync
  | FormQuestionRichText
  | FormQuestionMultiSelectTab
  | FormQuestionPhone
  | FormQuestionTimeRange
  | FormQuestionEquation
  | FormQuestionVideo

export type FormInputType = FormQuestionType & {
  display?: 'input' | undefined
  Component?: React.ComponentType<{ input: FormInputType }>
}

export type FormInputTypeText = FormInputType & { type: FormQuestionText['type'] }
export type FormInputTypeTextarea = FormInputType & { type: FormQuestionTextarea['type'] }
export type FormInputTypeMultiCheckbox = FormInputType & { type: FormQuestionMultiCheckbox['type'] }
export type FormInputTypeMultiSelect = FormInputType & { type: FormQuestionMultiSelect['type'] }
export type FormInputTypeMultiSelectTab = FormInputType & {
  type: FormQuestionMultiSelectTab['type']
}
export type FormInputTypeRadio = FormInputType & { type: FormQuestionRadio['type'] }
export type FormInputTypeDate = FormInputType & { type: FormQuestionDate['type'] }
export type FormInputTypeNumber = FormInputType & { type: FormQuestionNumber['type'] }
export type FormInputTypeSelect = FormInputType & { type: FormQuestionSelect['type'] }
export type FormInputTypeSelectGroup = FormInputType & { type: FormQuestionSelectGroup['type'] }
export type FormInputTypeSelectTab = FormInputType & { type: FormQuestionSelectTab['type'] }
export type FormInputTypeSelectAsync = FormInputType & { type: FormQuestionSelectAsync['type'] }
export type FormInputTypeRichText = FormInputType & { type: FormQuestionRichText['type'] }
export type FormInputTypePhone = FormInputType & { type: FormQuestionPhone['type'] }
export type FormInputTypeEquation = FormInputType & { type: FormQuestionEquation['type'] }
export type FormInputTypeVideo = FormInputType & { type: FormQuestionVideo['type'] }
export type FormInputTypeTimeRange = FormInputType & { type: FormQuestionTimeRange['type'] }

export type FormResult = FormQuestionType & {
  display: 'result'
  Component?: React.ComponentType<{ input: FormResult }>
  response: FormValue
}

export type FormDisplayBlock = FormContentBlock // union type, can add more here
export type FormBlock = FormInputType | FormResult | FormDisplayBlock

export type FormQuestion = Exclude<FormBlock, { display: 'display' }>

export type FormInputWithOptions = Exclude<
  FormInputType,
  (FormInputType & { options?: undefined }) | FormQuestionSelectGroup
>

export type FormInputs = FormInputType[]

export type FormBlocks = FormBlock[]

export type FormRow = {
  blocks: FormBlock[]
  key: string
}

export type RenderQuestion = (input: FormQuestion) => JSX.Element

export type AlignInputs = 'center'

export type FormBlocksComponentProps = {
  rows: FormRow[]
  errors: FormErrors
  renderQuestion: RenderQuestion
  alignInputs?: AlignInputs
}

export type FormOnValidate = (
  values: FormValues,
  errors: FormErrors | null
) =>
  | { errors: FormErrors; message: string | null }
  | null
  | Promise<{ errors: FormErrors; message: string | null } | null>
export type FormOnChange = (name: string, value: FormValue, values: FormValues) => void
export type FormOnSubmit = (values: FormValues) => void | Promise<void>

export type FormValidationResult = {
  success: boolean
  message: string
  errors: FormErrors | null
}

export const FormContext = React.createContext<{
  disabled: boolean
  submitting: boolean
  direction: FormDirection
  errors: FormErrors
  onChange: (name: string, value: FormValue) => void
  onSubmit: () => void
}>({
  disabled: false,
  submitting: false,
  direction: 'vertical',
  errors: {},
  onChange: noOp,
  onSubmit: noOp,
})
